import * as React from "react";
import * as S from "@styled";
import * as _ from "lodash";
import * as AppPath from "../model/AppPath";

import { IProcessListColumnVisibilityControlProps, IExecutionsPerDayCellProps, INameCellProps, ProcessRow } from "processList/model/ProcessList";
import { ClickEvent } from "shared/model/Event";
import { getDraftEditLink, getEditLink } from "processList/model/AppLinks";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparklines, SparklinesLine } from "react-sparklines";
import colors from "shared/styles/colors";
import { ProcessWithStatus } from "processList/model/ProcessWithStatus";
import { FilterCategory, ListState } from "processList/model/ListState";
import { CellContext } from "@tanstack/react-table";
import { connect } from "react-redux";
import { State } from "processList/model/State";
import { ThemeType } from "shared/styles/theme";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { DropdownColumnVisibilityControl } from "shared/components/Table";

const ProcessDescription: React.FC<{ description: string }> = (props) => {
    return <ReactMarkdown
        className="process-description react-markdown-description"
        remarkPlugins={[remarkGfm]}
        components={{
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />
        }}
    >
        {props.description}
    </ReactMarkdown>
}
const MemoizedProcessDescription = React.memo(ProcessDescription);



export const NameCell: React.FC<INameCellProps> = (props) => {
    const stopClickPropagation = (e: ClickEvent<HTMLAnchorElement>) => e.stopPropagation();
    const [theme] = useUserTheme();
    const nameLink = props.possibleDraftId ? (
        <S.ProcessRowLink
            className="process-name"
            id={`process-name-${props.getValue()}-selector`}
            title={FRENDS.Localizations.Edit}
            href={getDraftEditLink(props.possibleDraftId)}
            onClick={stopClickPropagation}
        >
            {props.getValue()}
            <span>&nbsp;<small><i className="ci-edit"></i> {FRENDS.Localizations.Draft}</small></span>
        </S.ProcessRowLink>
    ) : (
        <S.ProcessRowLink
            className="process-name"
            id={`process-name-${props.getValue()}-selector`}
            title={FRENDS.Localizations.Edit}
            href={getEditLink(props.cell.row.original.id)}
            onClick={stopClickPropagation}
        >
            {props.getValue()}
        </S.ProcessRowLink>
    );

    const versionDescription = (
        <S.ProcessRowVersion className="pointer process-version" title={FRENDS.Localizations.ShowChangeLog} onClick={() => props.onShowChangeLogClick(props.cell.row.original.id)}>
            {props.isDeploying ? FRENDS.Localizations.UpdatingToVersion + " " : ""}
            {props.cell.row.original.majorVersion}.{props.cell.row.original.minorVersion}.{props.cell.row.original.version}
        </S.ProcessRowVersion>
    );

    return (
        <S.FlexContainer style={{ alignItems: "center" }}>
            {props.isPinned && (
                <div style={{ height: "100%", alignSelf: "center" }} className="process-pinned-selector">
                    <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.6692 0.79573L4.33539 0.795729C4.08916 0.79573 3.86756 0.896113 3.70657 1.05711C3.54557 1.2181 3.44519 1.4397 3.44519 1.68593L3.44708 7.79041C3.18949 7.99496 2.94516 8.21656 2.7122 8.44953C1.32576 9.83596 0.395791 11.6789 0.170401 13.7301C0.156818 13.8544 0.169568 13.9801 0.207822 14.0992C0.246076 14.2182 0.308975 14.3278 0.392422 14.4209C0.475869 14.514 0.577992 14.5885 0.692138 14.6395C0.806285 14.6905 0.929894 14.7169 1.05492 14.7169L7.93784 14.7169L7.93405 22.8726C7.93374 22.9723 7.96862 23.0689 8.03254 23.1454L8.67273 23.9143C8.83372 24.1094 9.1273 24.1189 9.30344 23.9427C9.31291 23.9333 9.32238 23.9238 9.32996 23.9124L9.97014 23.1435C10.0345 23.0677 10.0686 22.9692 10.0686 22.8707L10.0686 14.7112H16.9516C17.1978 14.7112 17.4194 14.6108 17.5804 14.4498C17.7641 14.2661 17.8664 14.0047 17.8361 13.7244C17.5709 11.3266 16.3474 9.21661 14.5556 7.78472L14.5556 1.68214C14.5575 1.19537 14.1597 0.797625 13.6692 0.79573ZM12.6294 7.7904L12.6294 8.71848L13.3529 9.29806C14.4735 10.1929 15.2904 11.4121 15.692 12.7888L2.31255 12.785C2.42241 12.4062 2.56257 12.0387 2.73682 11.6826C3.07396 10.9894 3.52474 10.3606 4.07401 9.81134C4.25584 9.62951 4.44903 9.45526 4.65169 9.29427L5.37521 8.71469V7.78662L5.37521 2.72196L12.6294 2.72196L12.6294 7.7904Z" fill={`${theme === ThemeType.light ? "black" : "white"}`} />
                    </svg>
                    &nbsp;
                </div>
            )}
            <div style={{ height: "100%" }}>
                {nameLink}&nbsp;
                <S.ProcessRowVersion>
                    {versionDescription}
                </S.ProcessRowVersion>
                <S.ProcessRowDescription>
                    <MemoizedProcessDescription description={props.cell.row.original.description} />
                </S.ProcessRowDescription>
            </div>
        </S.FlexContainer>
    )

}


export const ExecutionsPerDayCell: React.FC<IExecutionsPerDayCellProps> = (props) => {
    const [theme] = useUserTheme();
    let cellContent: JSX.Element | null = null;
    if (props.executionsPerDay && props.executionsPerDay.length > 0) {
        const tooltip = FRENDS.Localizations.LatestInstancesPerDay +
            `\n${FRENDS.Localizations.Today}: ${props.executionsPerDay[props.executionsPerDay.length - 1]}`;
        cellContent =
            <div title={tooltip}>
                <Sparklines
                    data={props.countsTimeLimitDays === 1 ? [0, props.executionsPerDay[props.executionsPerDay.length - 1]] : props.executionsPerDay}
                    // ACC-8011: Even if we choose 1 day we need two data points for the Sparkline to render properly
                    limit={props.countsTimeLimitDays === 1 ? 2 : props.countsTimeLimitDays}
                    svgWidth={180}
                    svgHeight={40}
                    margin={9}
                >
                    <SparklinesLine style={{
                        stroke: theme === ThemeType.light ? colors.purplePrimary : colors.purpleSecondary,
                        strokeWidth: 2,
                        fill: theme === ThemeType.light ? colors.purpleBackground : colors.dark2,
                        fillOpacity: 1,
                    }}
                    />
                    <SparklineAnimatedSpot />
                </Sparklines>
            </div>;
    }
    return cellContent;
}

export const TagCellConnected: React.FC<TagCellProps> = (props) => {
    if (_.isEmpty(props.cell.getValue())) {
        return null;
    }

    const tags = props.cell.getValue();

    const handleClick = (tag: string) => {
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({
            filterCategory: FilterCategory.Tags,
            filterText: tag,
            page: null
        }));
    }

    return (
        <S.FlexContainer gap={5}>
            {tags.map((tag, i) => (
                <S.ProcessTagButton key={i} id={`tag-${tag}-selector`} onClick={() => handleClick(tag)}><S.ProcessTagHole />{tag}</S.ProcessTagButton>
            ))}
        </S.FlexContainer>
    )
}

export const ColumnVisibilityControl: React.FC<IProcessListColumnVisibilityControlProps<keyof ProcessWithStatus>> = (props) => (
    <DropdownColumnVisibilityControl {...props} />
);

class SparklineAnimatedSpot extends React.Component<SparklineAnimatedSpotProps, { showSpot: boolean, spotData: any }> {

    public static getDerivedStateFromProps(props: SparklineAnimatedSpotProps, state: { showSpot: boolean, spotData: any }) {
        if (!_.isEqual(props, state.spotData) && !state.showSpot) {
            return {
                showSpot: true,
                spotData: props
            };
        }
        return {
            showSpot: false
        };
    }

    public constructor(props: SparklineAnimatedSpotProps) {
        super(props);
        this.state = {
            showSpot: false,
            spotData: props
        };
    }

    public render() {
        if (!this.props.points || !this.state.showSpot) {
            return null;
        }
        const lastPoint = this.props.points[this.props.points.length - 1];
        return <g>
            <circle
                key={lastPoint.y + this.state.showSpot.toString()}
                className="sparkline-change-dot"
                cx={lastPoint.x}
                cy={lastPoint.y}
                r={3}
                fill="#000"
            >
            </circle>
        </g>;
    }
}

interface SparklineAnimatedSpotProps {
    points?: [{ x: number, y: number }]; // set by sparkline parent
}

interface TagCellOwnProps {
    cell: CellContext<ProcessRow, string[]>
}

interface TagCellStateProps {
    listState: ListState;
}


type TagCellProps = TagCellOwnProps & TagCellStateProps;

const mapStateToProps = (state: State, ownProps: TagCellOwnProps): TagCellStateProps => {
    return {
        listState: state.listState
    }
}


export const TagCell = connect(mapStateToProps)(TagCellConnected);